import { useReducedMotion } from "motion/react";

export type OnboardingVibesVariant = 1 | 2 | 3 | 4 | "setup";

type OnboardingVibesOverlayProps = {
  variant: OnboardingVibesVariant;
};

type WaveSpec = {
  d: string;
  delay: string;
  opacity: number;
};

const WAVE_PALETTE = [
  "oklch(78% 0.14 85)",
  "oklch(72% 0.16 72)",
  "oklch(68% 0.12 95)",
] as const;

const VARIANT_WAVES: Record<OnboardingVibesVariant, WaveSpec[]> = {
  1: [
    { d: "M72 18 C 88 28, 94 42, 90 58", delay: "0s", opacity: 0.55 },
    { d: "M76 22 C 96 34, 100 50, 92 68", delay: "0.35s", opacity: 0.4 },
    { d: "M68 16 C 82 30, 86 48, 78 62", delay: "0.7s", opacity: 0.32 },
    { d: "M80 26 C 102 40, 106 58, 98 76", delay: "1.05s", opacity: 0.28 },
  ],
  2: [
    { d: "M48 92 C 34 78, 28 62, 32 46", delay: "0s", opacity: 0.52 },
    { d: "M52 96 C 30 80, 22 58, 28 38", delay: "0.4s", opacity: 0.38 },
    { d: "M44 88 C 38 72, 40 56, 48 42", delay: "0.8s", opacity: 0.3 },
  ],
  3: [
    { d: "M18 52 C 8 40, 6 28, 14 18", delay: "0s", opacity: 0.48 },
    { d: "M82 48 C 92 36, 94 24, 86 14", delay: "0.3s", opacity: 0.48 },
    { d: "M14 58 C 4 44, 2 30, 10 20", delay: "0.65s", opacity: 0.34 },
    { d: "M86 54 C 96 40, 98 26, 90 16", delay: "0.95s", opacity: 0.34 },
  ],
  4: [
    { d: "M26 24 C 14 36, 10 52, 18 66", delay: "0s", opacity: 0.5 },
    { d: "M22 20 C 8 34, 4 54, 14 72", delay: "0.45s", opacity: 0.36 },
    { d: "M30 28 C 20 42, 18 58, 24 70", delay: "0.9s", opacity: 0.28 },
  ],
  setup: [
    { d: "M50 88 C 36 72, 32 54, 38 38", delay: "0s", opacity: 0.46 },
    { d: "M54 90 C 64 74, 70 56, 66 40", delay: "0.35s", opacity: 0.38 },
    { d: "M46 86 C 28 70, 24 50, 30 34", delay: "0.7s", opacity: 0.3 },
  ],
};

export function OnboardingVibesOverlay({ variant }: OnboardingVibesOverlayProps) {
  const prefersReducedMotion = useReducedMotion();

  if (prefersReducedMotion) {
    return null;
  }

  const waves = VARIANT_WAVES[variant];

  return (
    <svg
      className="pointer-events-none absolute inset-0 z-10 overflow-visible"
      viewBox="0 0 100 125"
      preserveAspectRatio="xMidYMid meet"
      aria-hidden
    >
      <defs>
        <filter id={`onboarding-vibes-glow-${variant}`} x="-20%" y="-20%" width="140%" height="140%">
          <feGaussianBlur stdDeviation="0.6" result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>

      {waves.map((wave, index) => (
        <path
          key={`${variant}-${index}`}
          d={wave.d}
          fill="none"
          stroke={WAVE_PALETTE[index % WAVE_PALETTE.length]}
          strokeWidth={1.1}
          strokeLinecap="round"
          opacity={wave.opacity}
          filter={`url(#onboarding-vibes-glow-${variant})`}
          className="onboarding-vibes-wave"
          style={{ animationDelay: wave.delay }}
        />
      ))}
    </svg>
  );
}
